/**
 * useScrollRestore ✦ — iter197
 * ─────────────────────────────────────────────────────────────
 * "Back should feel like moving through memory, not navigating
 *  software."
 *
 * Restores window.scrollY synchronously on remount so a guest
 * returning to a list-style surface (BoothLauncher, MemoryWall,
 * Archive) lands EXACTLY where their eyes last rested — no jump
 * to top, no flicker.
 *
 * Implementation contract:
 *   • useLayoutEffect → runs BEFORE the browser paints, so the
 *     restored position is the first frame the user sees.
 *   • sessionStorage → tab-scoped, dies on close. Survives back
 *     navigation, browser back, and tab-switch suspend.
 *   • Throttled scroll listener (rAF) → cheap, never blocks UI.
 *
 * Usage:
 *
 *   const { markRevealed } = useScrollRestore(`booth_launcher_${event_id}`);
 *
 * If the key is null/undefined the hook is a no-op — safe to use
 * before an id is resolved.
 */
import { useEffect, useLayoutEffect, useRef } from 'react';

const PREFIX = 'eventdrop_scroll_';

const readScroll = (key) => {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(`${PREFIX}${key}`);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch { return null; }
};

const writeScroll = (key, y) => {
  if (!key) return;
  try { sessionStorage.setItem(`${PREFIX}${key}`, String(Math.max(0, Math.round(y)))); }
  catch { /* private mode — no-op */ }
};

export function useScrollRestore(key) {
  const frameRef = useRef(0);

  // Synchronous restore BEFORE first paint — no visual flash.
  useLayoutEffect(() => {
    if (!key || typeof window === 'undefined') return;
    const y = readScroll(key);
    if (y === null) return;
    // Two phases — immediate (catches static layouts) + next frame
    // (catches layouts whose final height resolves after images /
    // suspense settle). Both use 'instant' to avoid smooth-scroll
    // animations betraying the restore.
    try { window.scrollTo({ top: y, left: 0, behavior: 'instant' }); }
    catch { window.scrollTo(0, y); }
    requestAnimationFrame(() => {
      try { window.scrollTo({ top: y, left: 0, behavior: 'instant' }); }
      catch { window.scrollTo(0, y); }
    });
  }, [key]);

  // Persist throttled via rAF so we never queue more than one
  // write per paint.
  useEffect(() => {
    if (!key || typeof window === 'undefined') return undefined;
    const onScroll = () => {
      if (frameRef.current) return;
      frameRef.current = requestAnimationFrame(() => {
        writeScroll(key, window.scrollY);
        frameRef.current = 0;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      // Final flush — captures the exact scrollY at unmount even if
      // the last rAF hadn't fired yet.
      writeScroll(key, window.scrollY);
    };
  }, [key]);
}

export default useScrollRestore;
